/**
 * Natural Language Query Parser
 * Parses natural language queries into structured filters and chips
 * 
 * Examples:
 * - "instagram items from last week" → { source: 'Instagram', time: 'last week' }
 * - "tiktok videos i uploaded yesterday" → { source: 'TikTok', type: 'video', time: 'yesterday' }
 * - "images tagged workspace from October" → { type: 'image', tag: 'workspace', time: 'October' }
 */

const SOURCE_PATTERNS = [
  { pattern: /\b(instagram|ig)\b/gi, value: 'Instagram' },
  { pattern: /\b(youtube|yt)\b/gi, value: 'YouTube' },
  { pattern: /\b(tiktok|tt)\b/gi, value: 'TikTok' },
  { pattern: /\b(web|website|url)\b/gi, value: 'Web' },
  { pattern: /\b(twitter|x)\b/gi, value: 'Twitter' },
];

const TYPE_PATTERNS = [
  { pattern: /\b(videos?|video content)\b/gi, value: 'videos' },
  { pattern: /\b(images?|photos?|pictures?)\b/gi, value: 'images' },
  { pattern: /\b(notes?|text|texts?)\b/gi, value: 'notes' },
  { pattern: /\b(articles?|links?|urls?)\b/gi, value: 'articles' },
  { pattern: /\b(pdfs?|documents?)\b/gi, value: 'pdfs' },
];

const TIME_PATTERNS = [
  { pattern: /\b(today)\b/gi, label: 'today' },
  { pattern: /\b(yesterday)\b/gi, label: 'yesterday' },
  { pattern: /\b(last week|past week)\b/gi, label: 'last week' },
  { pattern: /\b(this week)\b/gi, label: 'this week' },
  { pattern: /\b(last month|past month)\b/gi, label: 'last month' },
  { pattern: /\b(this month)\b/gi, label: 'this month' },
  { pattern: /\b(\d+)\s*(days?|d)\s*(ago)\b/gi, label: (match) => `${match[1]} days ago` },
  { pattern: /\b(from|in)\s+(october|november|december|january|february|march|april|may|june|july|august|september)\b/gi, label: (match) => match[2] },
];

const TAG_PATTERNS = [
  { pattern: /\b(about|regarding|concerning)\s+([a-z]+(?:\s+[a-z]+)*)\b/gi, group: 2 },
  { pattern: /\b(tagged|with tag|tag)\s+([a-z]+(?:\s+[a-z]+)*)\b/gi, group: 2 },
  { pattern: /\b(mentioning|containing)\s+([a-z]+(?:\s+[a-z]+)*)\b/gi, group: 2 },
];

/**
 * Parse a natural language query into structured filters
 * @param {string} query - Natural language query string
 * @returns {Object} Parsed filters and remaining text
 */
export function parseNaturalQuery(query) {
  if (!query || typeof query !== 'string') {
    return { filters: [], remainingText: query || '' };
  }

  const normalized = query.toLowerCase().trim();
  let remainingText = query;
  const filters = [];

  // Parse source
  for (const { pattern, value } of SOURCE_PATTERNS) {
    const match = remainingText.match(pattern);
    if (match) {
      filters.push({
        id: crypto.randomUUID(),
        label: value,
        kind: 'channel',
        originalMatch: match[0],
      });
      remainingText = remainingText.replace(pattern, '').trim();
      break;
    }
  }

  // Parse content type
  for (const { pattern, value } of TYPE_PATTERNS) {
    const match = remainingText.match(pattern);
    if (match) {
      filters.push({
        id: crypto.randomUUID(),
        label: value,
        kind: 'type',
        originalMatch: match[0],
      });
      remainingText = remainingText.replace(pattern, '').trim();
      break;
    }
  }

  // Parse time/date
  for (const timePattern of TIME_PATTERNS) {
    const match = remainingText.match(timePattern.pattern);
    if (match) {
      const label = typeof timePattern.label === 'function' 
        ? timePattern.label(match) 
        : timePattern.label;
      filters.push({
        id: crypto.randomUUID(),
        label: label,
        kind: 'time',
        originalMatch: match[0],
      });
      remainingText = remainingText.replace(timePattern.pattern, '').trim();
      break;
    }
  }

  // Parse tags/descriptions
  for (const { pattern, group } of TAG_PATTERNS) {
    const match = remainingText.match(pattern);
    if (match && match[group]) {
      filters.push({
        id: crypto.randomUUID(),
        label: match[group].trim(),
        kind: 'term',
        originalMatch: match[0],
      });
      remainingText = remainingText.replace(pattern, '').trim();
      break;
    }
  }

  // Clean up remaining text (remove extra whitespace, common words)
  remainingText = remainingText
    .replace(/\b(i|uploaded|added|from|in|the|a|an)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    filters,
    remainingText,
  };
}

/**
 * Convert a natural language query to chips
 * @param {string} query - Natural language query
 * @param {Array} existingChips - Existing chips to merge with
 * @returns {Array} Array of chip objects
 */
export function queryToChips(query, existingChips = []) {
  if (!query || typeof query !== 'string') {
    return existingChips;
  }

  const { filters, remainingText } = parseNaturalQuery(query);
  const newChips = [...existingChips, ...filters];

  // Add remaining text as a term chip if it's meaningful
  if (remainingText && remainingText.length > 2) {
    newChips.push({
      id: crypto.randomUUID(),
      label: remainingText,
      kind: 'term',
    });
  }

  return newChips;
}






